import dayjs from 'dayjs';
import React, { Fragment, useCallback, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { Event } from '@mui/icons-material';
import {
  DateTimePickerProps,
  DesktopDateTimePicker,
  LocalizationProvider,
  MobileDateTimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';

import { CommonFieldProps, DateTimeFieldProps } from './props/FieldProps';
import { Title } from './Widgets/Title';
import { translate } from 'react-jhipster';

export interface StandardDateTimePickerProps extends CommonFieldProps<'date-time-picker'>, DateTimeFieldProps {
  attribute: Required<CommonFieldProps<'date-time-picker'>>['attribute'];
}

export default function StandardDateTimePicker(props: {
  field: StandardDateTimePickerProps;
  methods: UseFormReturn;
  hideTitle?: boolean;
}) {
  const {
    field: fieldConfig,
    methods: {
      control,
      getValues,
      setValue,
      trigger,
      formState: { errors },
    },
    hideTitle,
  } = props;
  const [open, setOpen] = useState<boolean>();

  const component = useCallback(
    (dateTimePickerProps: DateTimePickerProps) => {
      if (fieldConfig.keyboard) {
        return <DesktopDateTimePicker {...dateTimePickerProps} />;
      }
      return <MobileDateTimePicker {...dateTimePickerProps} />;
    },
    [fieldConfig.keyboard],
  );

  const componentProps = (fieldConf: StandardDateTimePickerProps, value?: string): DateTimePickerProps => {
    return {
      ampm: false,
      format: 'DD/MM/YYYY HH:mm',
      label: fieldConf.label && translate(fieldConf.label, null, fieldConf.label),
      open: !!open,
      onClose: () => setOpen(false),
      ...fieldConf.props,
      value: value ? dayjs(value, 'YYYY-MM-DD HH:mm:ss') : null,
      onChange(valueIn) {
        if (valueIn) {
          try {
            const formatted = dayjs(valueIn).format('YYYY-MM-DD HH:mm:00');
            setValue(fieldConf.attribute, formatted);
          } catch (error) {}
        } else {
          setValue(fieldConf.attribute, undefined);
        }
      },
      slotProps: {
        textField: {
          fullWidth: true,
          size: 'small',
          onClick: () => setOpen(true),
          error: !!errors[fieldConf.attribute],
          helperText: <>{errors[fieldConf.attribute]?.message}</>,
          onKeyDown(event) {
            if (event.key === 'Enter') {
              trigger(fieldConf.attribute);
            }
          },
          InputProps: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton aria-label="open date time picker" size="large">
                  <Event />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              pr: fieldConf.keyboard ? undefined : 0,
            },
          },
        },
      },
    };
  };

  return (
    <Controller
      name={fieldConfig.attribute}
      control={control}
      render={({ field }) => (
        <Fragment>
          {!hideTitle && fieldConfig.title && <Title field={fieldConfig} />}
          <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {component(componentProps(fieldConfig, field.value))}
            </LocalizationProvider>
          </Box>
        </Fragment>
      )}
    />
  );
}
